import { useState } from 'react';
import type { ProductId } from '@/core/content/ids';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSave, useServices } from '@/ui/services/ServicesContext';
import { newTxId } from '@/ui/util/ids';
import shell from './CatalogSheet.module.css';
import styles from './OrdersSheet.module.css';
import { useBack } from '@/ui/back/backStack';
import { Icon } from '@/ui/components/Icon';
import { SpotArt } from '@/ui/art/SpotArt';
import { useUiImage } from '@/ui/art/uiArt';

type Order = ReturnType<typeof useOrders>[number];

function useOrders() {
  return useSave((s) => s.orders.open);
}

/** S14 Bảng đơn (ux-screens §3.14): no deadlines, no timers; "Để sau" never costs anything. */
export function OrdersSheet({ onClose, onRestock }: { onClose: () => void; onRestock: () => void }) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const orders = useOrders();
  const stacks = useSave((s) => s.inventory.productStacks);
  const slots = useSave((s) => s.rooms.minimart.slots);
  const [confirmSkip, setConfirmSkip] = useState<Order | null>(null);
  useBack(() => {
    if (confirmSkip) setConfirmSkip(null);
    else onClose();
  });

  const shelved = (id: ProductId) =>
    Object.values(slots).filter((c) => c.kind === 'product' && c.itemId === id).length;
  const bag = (id: ProductId) => stacks[id] ?? 0;
  const templateText = (o: Order) => content.orderTemplates.get(o.templateId)?.textKey ?? '';

  return (
    <div
      className={shell.scrim}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className={`${shell.sheet} ${styles.board}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('ui.orders.title')}
      >
        <div className={shell.grip} aria-hidden="true" />
        <header className={shell.header}>
          <h2 className={shell.title}>{t('ui.orders.title')}</h2>
          <span className={styles.slots}>
            {t('ui.tasks.progress', { current: orders.length, target: content.balance.maxOpenOrders })}
          </span>
          <button
            type="button"
            className={shell.iconBtn}
            aria-label={t('ui.catalog.close')}
            onClick={onClose}
          >
            <Icon name="close" size={26} />
          </button>
        </header>

        {confirmSkip ? (
          <div className={styles.confirm} role="alertdialog" aria-label={t('ui.orders.skip')}>
            <p>
              {t('ui.orders.skip_confirm', {
                npc: t(content.npcs.get(confirmSkip.npcId)?.nameKey ?? ''),
              })}
            </p>
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => {
                  setConfirmSkip(null);
                }}
              >
                {t('ui.orders.keep')}
              </button>
              <button
                type="button"
                className={styles.primary}
                onClick={() => {
                  dispatch({ type: 'skipOrder', orderId: confirmSkip.orderId });
                  setConfirmSkip(null);
                }}
              >
                {t('ui.orders.let_go')}
              </button>
            </div>
          </div>
        ) : (
          <ul className={styles.list} data-testid="orders-list">
            {orders.length === 0 && (
              <li className={styles.empty}>
                <SpotArt name="empty_orders" fallback="orders" size={130} />
                <p>{t('ui.orders.empty')}</p>
              </li>
            )}
            {orders.map((o) => {
              const npc = content.npcs.get(o.npcId);
              const ready = o.lines.every((l) => bag(l.itemId) + shelved(l.itemId) >= l.qty);
              return (
                <li key={o.orderId} className={styles.card} data-ready={ready || undefined}>
                  <div className={styles.who}>
                    <NpcFace
                      npcId={o.npcId}
                      initial={
                        t(npc?.nameKey ?? '')
                          .split(' ')
                          .pop()
                          ?.slice(0, 1) ?? ''
                      }
                    />
                    <span className={styles.name}>{t(npc?.nameKey ?? '')}</span>
                  </div>
                  <p className={styles.quote}>“{t(templateText(o))}”</p>
                  <ul className={styles.lines}>
                    {o.lines.map((l) => {
                      const fromBag = Math.min(bag(l.itemId), l.qty);
                      const have = Math.min(l.qty, bag(l.itemId) + shelved(l.itemId));
                      const fromShelf = have - fromBag;
                      return (
                        <li key={l.itemId} data-ok={have >= l.qty || undefined}>
                          <span>{t(content.items.get(l.itemId)?.nameKey ?? '')}</span>
                          <span className={styles.qty}>
                            {t('ui.tasks.progress', { current: have, target: l.qty })}
                            {have >= l.qty && <Icon name="check" size={16} className={styles.tick} />}
                            {have >= l.qty
                              ? ` ${t('ui.orders.from', { bag: fromBag, shelf: fromShelf })}`
                              : ` ${t('ui.orders.missing', { count: l.qty - have })}`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <p className={styles.reward}>
                    {t('ui.orders.reward', { coins: o.rewardCoins, friendship: o.friendship })}
                  </p>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.secondary}
                      onClick={() => {
                        setConfirmSkip(o);
                      }}
                    >
                      {t('ui.orders.skip')}
                    </button>
                    {ready ? (
                      <button
                        type="button"
                        className={styles.primary}
                        data-testid={`order-fulfill-${o.orderId}`}
                        onClick={() => {
                          dispatch({ type: 'fulfillOrder', txId: newTxId(), orderId: o.orderId });
                        }}
                      >
                        {t('ui.orders.deliver')}
                      </button>
                    ) : (
                      <button type="button" className={styles.secondary} onClick={onRestock}>
                        {t('ui.orders.restock')}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!confirmSkip && orders.length > 0 && <p className={styles.footer}>{t('ui.orders.footer')}</p>}
      </section>
    </div>
  );
}

/** The neighbour's painted face (dialogue portrait, cropped round); their initial until the art loads. */
export function NpcFace({ npcId, initial }: { npcId: string; initial: string }) {
  const url = useUiImage(`por_${npcId}_neutral`);
  return (
    <span className={styles.avatar} aria-hidden="true">
      {url ? <img src={url} alt="" draggable={false} /> : initial}
    </span>
  );
}
